import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { X, Heart, MessageSquare } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import useScreenshotDeter from "../hooks/useScreenshotDeter";
import BlackoutCurtain from "../components/BlackoutCurtain";
import ForensicOverlay from "../components/ForensicOverlay";

const EMOJIS = ["❤️", "🔥", "😍", "🤤", "👏"];

export default function GlimtViewer() {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [msg, setMsg] = useState("");
  const timerRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get(`/glimt/skaper/${username}`).then(r => {
      setCreator(r.data.creator); setItems(r.data.items || []);
    }).catch(e => { toast.error(formatApiError(e)); navigate("/"); });
  }, [username]);

  const cur = items[idx];
  const isVideo = cur?.media_type === "video";
  const deter = useScreenshotDeter({ label: isVideo ? "glimt-video" : "glimt-image", enabled: !!cur && !isVideo });

  useEffect(() => {
    if (!cur) return;
    api.post(`/glimt/${cur.id}/view`).catch(() => {});
    if (cur.media_type === "image") {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => next(), 8000);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line
  }, [cur?.id]);

  const next = () => {
    if (idx < items.length - 1) setIdx(idx + 1);
    else navigate(-1);
  };
  const prev = () => { if (idx > 0) setIdx(idx - 1); };
  const react = async (emoji, message) => {
    await api.post(`/glimt/${cur.id}/react`, { emoji, message });
    toast.success("Sendt");
    setMsg("");
  };

  if (!creator || items.length === 0) return <div className="fixed inset-0 bg-black flex items-center justify-center text-white">Laster…</div>;

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" data-testid="glimt-viewer">
      <div className="absolute top-0 left-0 right-0 z-30 p-4 flex items-center gap-2 bg-gradient-to-b from-black/80 to-transparent">
        <img src={creator.avatar_url || "/logo192.png"} alt="" className="w-8 h-8 rounded-full object-cover" />
        <span className="font-fraunces italic text-[var(--cream)]">@{creator.username}</span>
        <div className="flex-1 flex gap-1 mx-4">
          {items.map((_, i) => (
            <div key={i} className={`h-0.5 flex-1 ${i < idx ? "bg-white" : i === idx ? "bg-white/70" : "bg-white/20"}`} />
          ))}
        </div>
        <button onClick={() => navigate(-1)}><X className="h-6 w-6 text-white" /></button>
      </div>

      <div className="absolute left-0 top-1/4 bottom-1/4 w-1/3 z-10" onClick={prev} />
      <div className="absolute right-0 top-1/4 bottom-1/4 w-1/3 z-10" onClick={next} />

      {cur.media_type === "video" ? (
        <video key={cur.id} src={`${backend}/api/glimt/${cur.id}/media`} className="max-w-full max-h-full object-contain" autoPlay controls onEnded={next} controlsList="nodownload" disablePictureInPicture />
      ) : (
        <div className="relative max-w-full max-h-full">
          <img src={`${backend}/api/glimt/${cur.id}/media`} alt="" className="max-w-full max-h-full object-contain" />
          <ForensicOverlay viewId={`glimt-${cur.id}`} testId="glimt-forensic" />
        </div>
      )}

      <BlackoutCurtain active={deter.blackout} testId="glimt-blackout" message="Beskyttet innhold — glimt vises kun med aktivt fokus" />

      {cur.caption && (
        <div className="absolute bottom-24 left-4 right-4 z-20 text-center">
          <p className="font-manrope text-sm text-white drop-shadow-lg">{cur.caption}</p>
        </div>
      )}

      <div className="absolute bottom-4 left-4 right-4 z-30 flex flex-col gap-2">
        <div className="flex gap-2 justify-center">
          {EMOJIS.map(e => (
            <button key={e} onClick={() => react(e, null)} className="text-2xl p-2 bg-black/40 hover:bg-[var(--magenta)]/40 rounded-full">
              {e}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={msg} onChange={(ev) => setMsg(ev.target.value)} placeholder="Send melding til skaper…"
            className="flex-1 bg-black/60 border border-white/20 px-3 py-2 text-sm text-white" />
          <button onClick={() => msg.trim() && react(null, msg.trim())} disabled={!msg.trim()}
            className="btn-magenta px-3 text-sm"><MessageSquare className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}
